import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import apiToken, { handleApiError } from '@/shared/services/axiosInstance';
import { ProductCardData } from '@/shared/types/ProductCardTypes';
import { clampQuantity } from '@/shared/utils/quantityUtils';
import { useDropDown } from '@/shared/hooks/useDropDown';
import { useAuthStore } from '@/shared/stores/useAuthStore';
import { useProductQuantityInput } from './useProductQuantityInput';

import { ProductData, ProductOption } from '../types/ProductPageTypes';

export const useProductDetail = () => {
  // State management
  const [selectedOptions, setSelectedOptions] = useState<
    Array<{ option: ProductOption; quantity: number; selectedValue2?: string }>
  >([]);
  const optionsDropdown = useDropDown();
  const options2Dropdown = useDropDown();
  const [selectedOption1, setSelectedOption1] = useState<string>('');
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [productData, setProductData] = useState<ProductData>(
    {} as ProductData,
  );
  const [mainImage, setMainImage] = useState<string>('');
  const [youMayLikeList, setYouMayLikeList] = useState<ProductCardData[]>([]);
  const { handleInputChange, getInputValue, resetInputValue } =
    useProductQuantityInput(selectedOptions, {
      onQuantityChange: (index, quantity) => {
        setSelectedOptions((prev) =>
          prev.map((item, i) => (i === index ? { ...item, quantity } : item)),
        );
      },
      onValidationError: (message) => {
        alert(message);
      },
      debounceDelay: 1000,
    });

  const params = useParams();
  const { id } = params;

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const getProductDetail = async () => {
    try {
      const response = await apiToken.get(`/api/v1/product/detail/${id}`);
      setProductData(response.data.data);
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch product detail:', errorMessage);
      alert(
        'Failed to load product details. Please refresh the page and try again.',
      );
    }
  };

  useEffect(() => {
    if (id) {
      getProductDetail();
    }
  }, [id]);

  useEffect(() => {
    if (productData.product?.thumbnailUrlArray) {
      let arr: string[] = [];
      try {
        arr = JSON.parse(productData.product.thumbnailUrlArray);
      } catch {
        arr = [];
      }
      if (arr.length > 0) {
        setMainImage(arr[0]);
      }
    }
  }, [productData.product?.thumbnailUrlArray]);

  // Business logic functions
  const hasSecondOption = () => {
    return (
      productData.optionList?.[0]?.optionNameKey2 &&
      productData.optionList?.[0]?.optionNameKey2.trim() !== ''
    );
  };

  const isSingleSimpleOption = () => {
    return productData.optionList?.length === 1 && !hasSecondOption();
  };

  const addOptionToSelected = (option: ProductOption) => {
    const existingIndex = selectedOptions.findIndex(
      (selected) => selected.option.uuid === option.uuid,
    );
    if (existingIndex === -1) {
      const initialQuantity = option.minimumOrderQuantity || 1;
      setSelectedOptions((prev) => [
        ...prev,
        { option, quantity: initialQuantity },
      ]);
    }
  };

  const handleFirstOptionSelect = (optionValue1: string) => {
    setSelectedOption1(optionValue1);
    optionsDropdown.close();

    // If there's no second option, add the selected option directly
    if (!hasSecondOption()) {
      const selectedOption = productData.optionList?.find(
        (option) => option.optionNameValue1 === optionValue1,
      );
      if (selectedOption) {
        addOptionToSelected(selectedOption);
      }
    }
  };

  const handleSecondOptionSelect = (option: ProductOption) => {
    if (!option.isSoldOut) {
      addOptionToSelected(option);
      options2Dropdown.close();
    }
  };

  const removeOptionFromSelected = (index: number) => {
    setSelectedOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOptionQuantityChange = (index: number, delta: number) => {
    setSelectedOptions((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const minQuantity = item.option.minimumOrderQuantity || 1;
          const maxQuantity = item.option.stock || 0;
          const newQuantity = clampQuantity(
            item.quantity + delta,
            maxQuantity,
            minQuantity,
          );

          resetInputValue(index, newQuantity);

          return { ...item, quantity: newQuantity };
        }

        return item;
      }),
    );
  };

  const getUniqueOptionValues1 = () => {
    return Array.from(
      new Set(productData.optionList?.map((option) => option.optionNameValue1)),
    );
  };

  const getFilteredOptionsForSecond = () => {
    return productData.optionList?.filter(
      (option) => option.optionNameValue1 === selectedOption1,
    );
  };

  const calculateTotal = () => {
    return selectedOptions.reduce((total, selectedItem) => {
      const optionPrice = selectedItem.option.productPrice?.totalPrice || 1.0;
      return total + optionPrice * selectedItem.quantity;
    }, 0);
  };

  const getDefaultOption = () => {
    return (
      productData.currentOption ||
      productData.optionList?.find((option) => option.isDefault)
    );
  };

  const getDefaultPrice = () => {
    const defaultOption = getDefaultOption();
    return (
      defaultOption?.productPrice?.totalPrice || productData.totalPrice || 0
    );
  };

  const addToCart = async () => {
    if (selectedOptions.length === 0) {
      alert('Please select product options before adding to cart.');
      return;
    }
    if (isAuthenticated) {
      try {
        const productOptionAndQuantityList = selectedOptions.map((item) => ({
          productOptionUuid: item.option.uuid,
          quantity: item.quantity,
        }));

        await apiToken.post('/api/v1/cart', {
          productOptionAndQuantityList,
        });

        setIsCartModalOpen(true);
      } catch (error) {
        const errorMessage = handleApiError(error);
        if (errorMessage === 'Product is already in cart') {
          alert('Product is already in cart');
          return;
        }

        if (errorMessage === 'Unauthorized') {
          alert('Please log in to add items to your cart.');
          return;
        }

        console.error('Failed to add product to cart:', errorMessage);
        alert(errorMessage);
      }
    } else {
      alert('Please log in to add items to your cart.');
    }
  };

  const getProductList = async () => {
    try {
      const response = await apiToken.get(
        '/api/v1/product/list?page=1&limit=6&sort=latest&stockStatus=all',
      );
      setYouMayLikeList(response.data.data.productOptionList);
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      console.error('Error fetching product list:', errorMessage);

      if (errorMessage === 'Your account is pending approval.') {
        alert('Your account is pending approval. Please contact support.');
        useAuthStore.getState().clearToken();
      }
    }
  };

  useEffect(() => {
    getProductList();
  }, []);

  useEffect(() => {
    if (isSingleSimpleOption() && productData.optionList?.[0]) {
      const singleOption = productData.optionList[0];
      handleFirstOptionSelect(singleOption.optionNameValue1);
    }
  }, [productData.optionList]);

  return {
    // State
    selectedOptions,
    optionsDropdown,
    options2Dropdown,
    selectedOption1,
    isDetailsOpen,
    isCartModalOpen,
    productData,
    mainImage,
    youMayLikeList,

    // State setters
    setIsDetailsOpen,
    setIsCartModalOpen,
    setMainImage,
    // Business logic functions
    hasSecondOption,
    isSingleSimpleOption,
    addOptionToSelected,
    handleFirstOptionSelect,
    handleSecondOptionSelect,
    removeOptionFromSelected,
    handleOptionQuantityChange,
    handleInputChange,
    getInputValue,
    getUniqueOptionValues1,
    getFilteredOptionsForSecond,
    calculateTotal,
    getDefaultOption,
    getDefaultPrice,
    addToCart,
  };
};
